import React, { useContext, useEffect, useState } from 'react';
import { Col, Row, Select, Spin, Table } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { api, endpoints } from '../../api';
import { PdfViewerComponent } from '../../components/PdfViewerComponent/PdfViewerComponent';
import { routerPath } from '../../router/routerPath';
import { authContext } from '../../store/authStore/AuthContext';
import {
  BlueLayout,
  CustomButton,
  CustomSelect,
  CustomTableContainer,
  FormBlock,
  FormItem,
  GridItem,
  PagesContainerUI,
  TextLink,
} from '../../styles/styled';
import { NmsMeetingType } from '../CreateNmsMeetingPage/types';
import { PublishersType, SchoolClassesType } from '../NewApplicationPage/types';
import { InfoLayout } from '../ShowApplicationPage/components/TextBook/styled';
import { NmsInfoSearchType, NmsMeetingInfoResponseType, NmsMeetingInfosType, NmsMeetingInfoType } from './types';

const { Option } = Select;

export const NmsMeetingInfoPage = () => {
  const navigate = useNavigate();
  const { state } = useContext(authContext);
  const { id } = useParams();
  const [nmsMeetingsState, setNmsMeetings] = useState<NmsMeetingInfosType>([]);
  const [currentNmsState, setCurrentNmsState] = useState<NmsMeetingType | null>(null);
  const [openBookState, setOpenBook] = useState<string | undefined>(undefined);
  const [nmsTextbookTitlesState, setNmsTextbookTitles] = useState<string[]>([]);
  const [nmsTextbookPublishersState, setNmsTextbookPublishers] = useState<PublishersType>([]);
  const [nmsTextbookAuthorsState, setNmsTextbookAuthors] = useState<string[]>([]);
  const [nmsTextbookSchoolClassesState, setNmsTextbookSchoolClasses] = useState<SchoolClassesType>([]);
  const [searchInputState, setSearchInput] = useState<NmsInfoSearchType>({});
  const [searchState, setSearchState] = useState<'neutral' | 'clear' | 'search'>('clear');

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const nmsResponse = await api.get<NmsMeetingType>(endpoints.getNmsById(id));

          setSearchState('neutral');

          const nmsTextbooksTitlesResponse = await api.get<string[]>(endpoints.getNmsTextbookTitles(id));
          const nmsTextbookPublishersResponse = await api.get<PublishersType>(endpoints.getNmsTextbookPublishers(id));
          const nmsTextbookAuthorsResponse = await api.get<string[]>(endpoints.getNmsTextbookAuthors(id));
          const nmsTextbookSchoolClassesResponse = await api.get<SchoolClassesType>(
            endpoints.getNmsTextbookSchoolClasses(id),
          );

          setNmsTextbookAuthors(nmsTextbookAuthorsResponse.data);
          setNmsTextbookPublishers(nmsTextbookPublishersResponse.data);
          setNmsTextbookSchoolClasses(nmsTextbookSchoolClassesResponse.data);
          setNmsTextbookTitles(nmsTextbooksTitlesResponse.data);
          setCurrentNmsState(nmsResponse.data);
        } catch (err) {}
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchState === 'neutral') return;

    if (id) {
      (async () => {
        if (searchState === 'clear') {
          const response = await api.get<NmsMeetingInfoResponseType>(endpoints.getNmsTextbooksById({ id: id }));

          setNmsMeetings(
            response.data.nms_groups.map((el, idx) => {
              return { ...el, key: idx + 1 };
            }),
          );
        } else if (searchState === 'search') {
          const response = await api.get<NmsMeetingInfoResponseType>(
            endpoints.getNmsTextbooksById({ id: id, ...searchInputState }),
          );

          setNmsMeetings(
            response.data.nms_groups.map((el, idx) => {
              return { ...el, key: idx + 1 };
            }),
          );
        }
      })();
    }

    setSearchState('neutral');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState]);

  const nmsMeetingInfoColumns = [
    {
      title: '№ п/п',
      key: 'key',
      dataIndex: 'key',
    },
    {
      title: 'НАИМЕНОВАНИЕ УЧЕБНИКА',
      key: 'name',
      dataIndex: 'name',
      render: (value: string, nms: NmsMeetingInfoType) => <p>{nms?.textbook?.title}</p>,
    },
    {
      title: 'КЛАСС',
      key: 'school_classes',
      dataIndex: 'school_classes',
      render: (value: string, nms: NmsMeetingInfoType) => <p>{nms?.textbook?.school_classes[0].title}</p>,
    },
    {
      title: 'АВТОР (АВТОРСКИЙ КОЛЛЕКТИВ)',
      key: 'author',
      dataIndex: 'author',
      render: (value: string, nms: NmsMeetingInfoType) => <p>{nms?.textbook?.author}</p>,
    },
    {
      title: 'НАИМЕНОВАНИЕ ИЗДАТЕЛЬСТВА',
      key: 'publisher',
      dataIndex: 'publisher',
      render: (value: string, nms: NmsMeetingInfoType) => (
        <p>{nms?.textbook?.line_of_textbooks?.application?.publisher?.title}</p>
      ),
    },
    {
      title: 'МАТЕРИАЛЫ',
      key: 'materials',
      dataIndex: 'materials',
      render: (value: string, nmsMeeting: NmsMeetingInfoType) => {
        return (
          <GridItem direction="column" gap="5px" justify="flex-start" align="flex-start">
            {nmsMeeting?.textbook?.file_textbook?.file_path && (
              <button className="button-book" onClick={() => setOpenBook(nmsMeeting.textbook.file_textbook.file_path)}>
                <p className="text-link">Учебник</p>
              </button>
            )}
            {nmsMeeting?.textbook?.file_instruction?.file_path && (
              <button
                className="button-book"
                onClick={() => setOpenBook(nmsMeeting.textbook.file_instruction.file_path)}
              >
                <p className="text-link">Методическое пособие</p>
              </button>
            )}
            {nmsMeeting?.textbook?.file_guideline?.file_path && (
              <button className="button-book" onClick={() => setOpenBook(nmsMeeting.textbook.file_guideline.file_path)}>
                {' '}
                <p className="text-link">Экспертное заключение</p>
              </button>
            )}
            {currentNmsState?.nms_textbook_groups &&
              currentNmsState?.nms_textbook_groups
                .find((e) => e?.id === nmsMeeting.id)
                ?.files?.map(
                  (el, idx) =>
                    el?.file_path && (
                      <button key={el?.id} className="button-book" onClick={() => setOpenBook(el?.file_path)}>
                        <p className="text-link">Доп. материал {idx + 1}</p>
                      </button>
                    ),
                )}
          </GridItem>
        );
      },
    },
  ];

  useEffect(() => {
    if (openBookState) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
      window.scrollTo(0, 0);
    } else document.getElementsByTagName('body')[0].style.overflow = 'auto';
  }, [openBookState]);

  return (
    <PagesContainerUI>
      <br />
      {openBookState && <PdfViewerComponent closePdfViewer={() => setOpenBook(undefined)} bookLink={openBookState} />}
      <GridItem direction="column" justify="flex-start" align="flex-start" gap="5px">
        <BlueLayout padding="0">Заседание нмс</BlueLayout>
        <InfoLayout direction="row">
          Дата заседания:{' '}
          {currentNmsState?.date ? currentNmsState?.date?.split('-').reverse().join('.') : <Spin size="small" />}
        </InfoLayout>
        {state.isFederal && id && (
          <TextLink onClick={() => navigate(`${routerPath.editNmsMeeting}${id}`)}> Редактировать</TextLink>
        )}
      </GridItem>

      <FormBlock noBorderRadius>
        <Row gutter={[15, 15]}>
          <Col span={12}>
            <FormItem>
              <div className="text">Наименование учебника</div>
              <CustomSelect
                onChange={(value) => setSearchInput({ ...searchInputState, title: value as string })}
                value={searchInputState?.title}
              >
                {nmsTextbookTitlesState.map((el, idx) => (
                  <Option key={idx} value={el}>
                    {el}
                  </Option>
                ))}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem>
              <div className="text">Наименование издательства</div>
              <CustomSelect
                onChange={(value, option) =>
                  setSearchInput({ ...searchInputState, publisher: Object.values(option)[0] })
                }
                value={
                  nmsTextbookPublishersState.find((el) => el.id.toString() === searchInputState?.publisher?.toString())
                    ?.title
                }
              >
                {nmsTextbookPublishersState
                  .reduce<PublishersType>((sum, el) => (sum.find((e) => e.title === el.title) ? sum : [...sum, el]), [])
                  .map((el) => (
                    <Option key={el.id} value={el.title}>
                      {el.title}
                    </Option>
                  ))}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem>
              <div className="text">Автор (авторский коллектив)</div>

              <CustomSelect
                onChange={(value) => setSearchInput({ ...searchInputState, author: value as string })}
                value={searchInputState?.author}
              >
                {nmsTextbookAuthorsState.map((el, idx) => (
                  <Option key={idx} value={el}>
                    {el}
                  </Option>
                ))}
              </CustomSelect>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem>
              <div className="text">Класс</div>

              <CustomSelect
                onChange={(value) => setSearchInput({ ...searchInputState, school_classes: value as string })}
                value={searchInputState?.school_classes}
              >
                {nmsTextbookSchoolClassesState.map((el, idx) => (
                  <Option key={idx} value={el.title}>
                    {el.title}
                  </Option>
                ))}
              </CustomSelect>
            </FormItem>
          </Col>
        </Row>
        <GridItem align="center" justify="center" direction="row" gap="20px">
          <CustomButton onClick={() => setSearchState('search')}>Найти</CustomButton>
          <CustomButton
            onClick={() => {
              setSearchState('clear');
              setSearchInput({});
            }}
            lined
          >
            Очистить
          </CustomButton>
        </GridItem>
      </FormBlock>

      <CustomTableContainer>
        <Table columns={nmsMeetingInfoColumns} dataSource={nmsMeetingsState} />
      </CustomTableContainer>
    </PagesContainerUI>
  );
};
